/**
 * @file 模型库菜单的 hooks
 * @author FengGuang(fengguang01@baidu.com)
 */
import {useCallback, useEffect, useState} from 'react';
import {useLatest} from 'react-use';
import omit from 'object.omit';
import immer from 'immer';

import {IModelBaseListGroup, IModelBaseMenu} from './type.d';

import {
    MODEL_BASE_MENU_HOT
} from './constants';
import {fetchGetMenuList, fetchGetModelList} from '../../api/pages/modelBaseApi';
import {IGetModeBaselListParams, IModelBaseListItem} from '../../pages/modelbase/type';
import {getScrollTop} from '../../utils/get-scroll';


// 解析页面参数
// const menuSelected = (): IGetModeBaselListParams => {
//     const urlSearch = new URLSearchParams(window.location.search.slice(1));
//     const selectedParam: IGetModeBaselListParams = {
//         categoryId: urlSearch.get('categoryId') || undefined,
//         isHot: urlSearch.get('isHot') || undefined
//     };
//     if (!urlSearch.get('categoryId')) {
//         selectedParam.isHot = '1';
//     }
//     return omit(selectedParam, value => value !== undefined);
// };

// 预请求
let preRequest: any = {
    fetchGetMenuList: fetchGetMenuList()
};

// 递归遍历寻找指定的 menu 选项
export const findMenuList = (
    list: IModelBaseMenu[],
    id: string
): IModelBaseMenu | undefined => {
    let resItem = undefined;
    list.some(item => {
        if (item.id === id) {
            resItem = item;
            return true;
        }
        if (item.children.length > 0) {
            resItem = findMenuList(item.children, id);

            return !!resItem;
        }
        return false;
    });
    return resItem;
};

// 递归遍历获得所有的 key
export const getAllMenuListId = (
    list: IModelBaseMenu[]
): string[] => {
    let res: string[] = [];
    list.forEach(item => {
        res.push(item.id);
        if (item.children.length > 0) {
            res = res.concat(getAllMenuListId(item.children));
        }
    });
    return res;
};

interface IInitState {
    menuList?: IModelBaseMenu[];
    menuActiveKeys?: string[];
    menuOpenKeys?: string[];
    modelList?: IModelBaseListGroup;
}

export const useMenuList = (initialState: IInitState = {}) => {
    const [menuList, setMenuList] = useState<IModelBaseMenu[]>(
        initialState.menuList || [MODEL_BASE_MENU_HOT]
    );
    const [menuActiveKeys, setMenuActiveKeys] = useState<string[]>(
        initialState.menuActiveKeys || []
    );
    const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(
        initialState.menuOpenKeys || []
    );
    const [modelList, setModelList] = useState<IModelBaseListGroup>(
        initialState.modelList || {}
    );
    const modelListRef = useLatest(modelList);

    useEffect(() => {
        const getMenuList = async (): Promise<IModelBaseMenu[]> => {
            const fetchFunc = await preRequest.fetchGetMenuList || fetchGetMenuList();
            delete preRequest.fetchGetMenuList;

            let res;
            try {
                res = await fetchFunc;
            }
            catch (err: any) {
                // ignore
            }
            if (res) {
                const list: any[] = res.body.result || [];
                const transMenu = (oldMenu: any[] = []): IModelBaseMenu[] => {
                    return oldMenu.map(item => {
                        return {
                            id: item.id || '',
                            name: item.name || '',
                            parentId: item.parentId || '',
                            children: transMenu(item.children)
                        };
                    });
                };

                const newList = transMenu(list);
                const newListWithHot = [MODEL_BASE_MENU_HOT, ...newList];
                // 默认展开所有有子分类的父分类
                setMenuOpenKeys((oldKeys) => {
                    if (oldKeys.length > 0) {
                        return oldKeys;
                    }
                    // 递归遍历所有 key
                    const runMenu = (menuList: IModelBaseMenu[], res: Set<string> = new Set()) => {
                        menuList.forEach((menu) => {
                            if (menu.children.length > 0) {
                                res.add(menu.id);
                                runMenu(menu.children, res);
                            }
                        });
                        return res;
                    };
                    return Array.from(runMenu(newList).values());
                });
                setMenuList(newListWithHot);
                return newListWithHot;
            }
            return [];
        };

        const getModelList = async (id?: string): Promise<void> => {
            const theId = id || MODEL_BASE_MENU_HOT.id;
            let selectedParam: IGetModeBaselListParams = {
                categoryId: theId
            };
            if (theId === MODEL_BASE_MENU_HOT.id || theId === undefined) {
                selectedParam.isHot = '1';
                selectedParam.categoryId = undefined;
            }
            selectedParam = omit(selectedParam, value => value !== undefined);

            const fetchFunc = fetchGetModelList(selectedParam);

            let res;
            try {
                res = await fetchFunc;
            }
            catch (err: any) {
                // ignore
            }
            if (res) {
                const list: any[] = res.body.result.data || [];

                const newList: IModelBaseListItem[] = list.map(item => ({
                    id: item.id,
                    categoryId: item.categoryId,
                    nameCn: item.nameCn,
                    nameEn: item.nameEn,
                    applyCase: item.applyCase,
                    categoryName: item.categoryName,
                    score: item.score,
                    summary: item.summary,
                    detailUrl: item.detailUrl
                }));
                setModelList(prevList => (
                    immer(prevList, draft => {
                        draft[theId] = newList;
                    })
                ));
            }
        };

        (async () => {
            const newMenuList = await getMenuList();
            const runMenuList = (list: IModelBaseMenu[]) => {
                list.forEach(item => {
                    getModelList(item.id);
                    if (item.children && item.children.length > 0) {
                        runMenuList(item.children);
                    }
                });
            };
            runMenuList(newMenuList);
        })();
    }, [setMenuList]);

    const onMenuActiveChange = useCallback((keys: string[]) => {
        setMenuActiveKeys(keys);
        if (getScrollTop() > 200) {
            window.scrollTo({top: 200});
        }
    }, []);

    // 如果展开父菜单，也会同时展示父菜单的列表
    const onMenuOpenChangeAndMenuActiveChange = useCallback((keys: string[]) => {
        setMenuOpenKeys((oldOpenKeys) => {
            const changeKey: string | undefined = (() => {
                if (oldOpenKeys.length <= keys.length) {
                    const oldOpenKeysSet = new Set(oldOpenKeys);
                    return keys.find(s => !oldOpenKeysSet.has(s));
                }
                else {
                    const keysSet = new Set(keys);
                    return oldOpenKeys.find(s => !keysSet.has(s));
                }
            })();
            if (changeKey && modelListRef.current[changeKey] && modelListRef.current[changeKey].length > 0) {
                setMenuActiveKeys([changeKey]);
            }
            return keys;
        });
    }, [modelListRef, setMenuOpenKeys, setMenuActiveKeys]);

    return {
        menuList,
        menuActiveKeys,
        menuOpenKeys,
        modelList,

        onMenuActiveChange,
        onMenuOpenChange: onMenuOpenChangeAndMenuActiveChange
    };
};
